"use client";

import { useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { authClient } from "@/lib/auth-client";

// Better Auth redirects failed Microsoft callbacks and magic-link
// verifications here as ?error=<code> (see errorCallbackURL in page.tsx).
const MESSAGES: Record<string, string> = {
  INVALID_TOKEN:
    "That sign-in link has expired or was already used. Each link works once, so request a new one.",
  account_not_linked:
    "We couldn't connect your Microsoft account to your existing profile. Sign in with email instead, or ask an administrator for help.",
  access_denied:
    "Microsoft sign-in was cancelled, or your account doesn't have access to this app.",
  unable_to_get_user_info:
    "We couldn't verify your Microsoft account. Please try again.",
};

export function signInErrorMessage(code: string) {
  return MESSAGES[code] ?? "Sign in failed. Please try again.";
}

export function SignInErrorAlert() {
  const code = useSearchParams().get("error");
  const router = useRouter();

  useEffect(() => {
    if (!code) return;
    // People reopen a used magic link in a browser that is already signed in.
    // Send them to the app rather than showing a stale error. replace() drops
    // ?error, so this can run at most once per failed sign-in.
    let cancelled = false;
    authClient
      .getSession()
      .then(({ data }) => {
        if (!cancelled && data?.user && !data.user.banned) router.replace("/");
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [code, router]);

  if (!code) return null;
  return (
    <Alert variant="destructive" className="mb-4">
      <AlertCircle />
      <AlertDescription>{signInErrorMessage(code)}</AlertDescription>
    </Alert>
  );
}
